import { useContext, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import AuthContext from "../../context/auth/AuthContext";
import GuarantorInviteSection, {
  GuarantorInvite,
} from "../../components/GuarantorInviteSection/GuarantorInviteSection";
import { sendUniversalMail } from "../../services/sharedService";
import { toast } from "../../helper/common";

type Props = {
  user: any;
  onBack: () => void;
  onFinish: () => Promise<void> | void;
};

export default function RenewGuarantorsStep({ user, onBack, onFinish }: Props) {
  const { _t } = useTranslator();
  const { user: authUser } = useContext(AuthContext) as any;
  const [invites, setInvites] = useState<GuarantorInvite[]>([]);
  const youName =
    `${user?.fname ?? ""} ${user?.lname ?? ""}`.trim() ||
    authUser?.fname ||
    "You";
  const youEmail = user?.email ?? authUser?.email ?? "";
  const initial = (s: string) => (s?.trim()?.[0] ?? "?").toUpperCase();
  const onboardingGuarantors: any[] = (
    user?.tenant_extra_field_id?.selected_onboarding_users ?? []
  ).filter((x: any) => x.type === "guarantor");
  const addedGuarantors: any[] = user?.gurantor_added_id ?? [];

  const [finishing, setFinishing] = useState(false);
  const handleFinish = async () => {
    if (finishing) return;
    setFinishing(true);
    try {
      for (const inv of invites) {
        try {
          await sendUniversalMail({
            receiverEmail: inv.email,
            senderId: localStorage.getItem("saveduserid"),
            emailType: "score",
            status: "processing",
            gurantor_relation: inv.relation,
          });
        } catch {}
      }
      if (invites.length) toast.success(_t("emailSent"));
      await onFinish();
    } finally {
      setFinishing(false);
    }
  };

  return (
    <div className="approvedModal popup-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 wrapper-modal modal-fixed-screen">
      <div
        className="popup-content2 position-relative popup-content bg-white required-doc-popup"
        style={{
          maxWidth: 640,
          width: "calc(100% - 32px)",
          borderRadius: 20,
          paddingTop: 56,
        }}
      >
        <div
          className="cursor-pointer goBackButtonWrapper"
          style={{ position: "absolute", top: 24, left: 24 }}
          onClick={onBack}
        >
          <img src="/images/arrow-right.svg" className="arrow-left" alt="" />
          <p>{_t("goBack", "Back")}</p>
        </div>

        <h2
          style={{
            margin: "12px 0 6px",
            fontSize: 20,
            fontWeight: 750,
            color: "#1a2e2c",
          }}
        >
          {_t("yourGuarantors", "Your guarantors")}
        </h2>
        <p
          style={{
            color: "#5a6b68",
            fontSize: 13,
            marginBottom: 18,
            lineHeight: 1.4,
          }}
        >
          {_t(
            "guarantorsStayConnected",
            "Your guarantors stay connected to your profile. Outdated profiles need updating too."
          )}
        </p>

        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "#eef2f0",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 13,
                color: "#253837",
              }}
            >
              {initial(youName)}
            </span>
            <div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <strong
                  style={{ color: "#1a2e2c", fontSize: 14, lineHeight: 1 }}
                >
                  {youName}
                </strong>
                <span
                  style={{
                    background: "#eef1ef",
                    borderRadius: 999,
                    padding: "3px 9px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#6b7a77",
                    lineHeight: 1,
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  {_t("you", "You")}
                </span>
              </div>
              <div style={{ color: "#6b7a77", fontSize: 12 }}>{youEmail}</div>
            </div>
          </div>

          {onboardingGuarantors.map((g: any, i: number) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                paddingLeft: 46,
                minHeight: 48,
              }}
            >
              <span
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "#eef2f0",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {initial(g.name)}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <strong
                    style={{ fontSize: 14, color: "#1a2e2c", lineHeight: 1 }}
                  >
                    {g.name}
                  </strong>
                  <span
                    style={{
                      background: "#eef1ef",
                      borderRadius: 999,
                      padding: "3px 9px",
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#6b7a77",
                      lineHeight: 1,
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    {_t("guarantor", "Guarantor")}
                  </span>
                </div>
                <div style={{ color: "#6b7a77", fontSize: 12 }}>{g.email}</div>
              </div>
            </div>
          ))}
          {addedGuarantors.map((item: any, i: number) => {
            const name =
              `${item?.guarantor_id?.fname ?? ""} ${item?.guarantor_id?.lname ?? ""}`.trim() ||
              "Guarantor";
            const email = item?.guarantor_id?.email ?? "";
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  paddingLeft: 46,
                  minHeight: 48,
                }}
              >
                <span
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: "#eef2f0",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {initial(name)}
                </span>
                <div style={{ flex: 1 }}>
                  <div
                    style={{ display: "flex", gap: 6, alignItems: "center" }}
                  >
                    <strong
                      style={{ fontSize: 14, lineHeight: 1, color: "#1a2e2c" }}
                    >
                      {name}
                    </strong>
                    <span
                      style={{
                        background: "#eef1ef",
                        borderRadius: 999,
                        padding: "3px 9px",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#6b7a77",
                        lineHeight: 1,
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      {_t("guarantor", "Guarantor")}
                    </span>
                  </div>
                  <div style={{ color: "#6b7a77", fontSize: 12 }}>{email}</div>
                </div>
              </div>
            );
          })}

          <GuarantorInviteSection
            currentUserEmail={youEmail}
            invites={invites}
            onChange={setInvites}
            _t={_t as any}
          />
        </div>

        <div
          style={{ height: 1, background: "#e5eae7", margin: "18px 0 14px" }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span style={{ color: "#6b7a77", fontSize: 12 }}>
            {_t(
              "manageGuarantorsAnytime",
              "You can manage your guarantors anytime from your profile"
            )}
          </span>
          <button
            type="button"
            onClick={handleFinish}
            disabled={finishing}
            className="btnNext"
            style={{
              borderRadius: 12,
              padding: "10px 22px",
              background: "#1a2e2c",
              color: "#fff",
              border: "none",
              fontWeight: 700,
              fontSize: 14,
              opacity: finishing ? 0.7 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {finishing && (
              <span
                style={{
                  width: 14,
                  height: 14,
                  border: "2px solid rgba(255,255,255,0.35)",
                  borderTopColor: "#fff",
                  borderRadius: "50%",
                  display: "inline-block",
                  animation: "spin 0.7s linear infinite",
                }}
                aria-hidden
              />
            )}
            {_t("finish", "Finish")}
          </button>
        </div>
      </div>
    </div>
  );
}
